import { useContext, useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import useMotorwayOffer, { StateSlug } from 'Utilities/hooks/useMotorwayOffer';
import useUrlVRM from 'Utilities/hooks/useUrlVRM';
import { useGetFinanceStatus } from 'Utilities/vehicles/hooks/useGetFinanceStatus';
import { useGetPaymentsStatuses } from 'Utilities/vehicles/hooks/useGetPaymentsStatuses';
import { useGetLatestPostSaleOffer } from 'Utilities/vehicles/hooks/useLatestPostSaleOffer';

import { APIResponseStatus } from 'Services/api.types';

import { useServiceFeeData } from 'Components/transactionFees/hooks/useServiceFeeData';
import { useServiceFeeEnabled } from 'Components/transactionFees/hooks/useServiceFeeEnabled';

import useGetPreferencesStatuses from '../../documentCapture/DealerDetails/hooks/useGetPreferencesStatuses';
import { NE_FUNDS_PAID_STATUSES } from '../../payments/Payments.helpers';
import { POST_SALE_PAGES_ALLOWED_STATUSES } from '../constants/collectionPageAccessStatuses';
import { resolveSellerAmountDetails, vehicleCollectionState$ } from '../VehicleCollectionSubjects';
import { VehicleCollectionState } from '../VehicleCollectionSubjects.types';

import { useGetBeneficiary } from './useGetBeneficiary';
import { useGetCollectionDetails } from './useGetCollectionDetails';

// This pub is reused on the VehicleCollection page and the DocumentCapture page. Be cautious when changing it.
export const useVehicleCollectionPub = () => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { valuationRef, vrm } = useUrlVRM({ optimisticReturn: true });
  const { params } = useRouteMatch<{ vrm: string }>() ?? {};
  const { offer, refetch, status: offerStatus } = useMotorwayOffer();

  const enquiryId = vehicle?.enquiry?.dpEnquiryId ?? undefined;

  const collectionPreferences = useGetPreferencesStatuses(enquiryId);
  const collectionDetails = useGetCollectionDetails(enquiryId);

  const slug = offer?.state?.slug;
  const vehicleProfilePath = `/${params?.vrm ?? vehicle?.vrm}/vehicle-details`;

  const hasPostSaleAllowedStatus = slug
    ? Object.values<StateSlug>(POST_SALE_PAGES_ALLOWED_STATUSES).includes(slug)
    : false;

  const {
    accountNumber,
    error: beneficiaryError,
    loading: isBeneficiaryLoading,
  } = useGetBeneficiary(valuationRef, hasPostSaleAllowedStatus);

  const {
    data: latestPostSaleOfferData,
    getLatestPostSaleOffer,
    isFetched: isLatestPostSaleOfferFetched,
  } = useGetLatestPostSaleOffer();

  const {
    data: financeStatusData,
    getFinanceStatus,
    isFetched: isFinanceStatusFetched,
  } = useGetFinanceStatus();

  const {
    data: paymentsStatusesData,
    getPaymentsStatuses,
    isFetched: isPaymentsStatusesFetched,
  } = useGetPaymentsStatuses();

  const isOfferLoading = offerStatus === APIResponseStatus.LOADING;
  const shouldFetchPaymentsData = enquiryId && hasPostSaleAllowedStatus;
  const shouldFetchPaymentStatus = enquiryId && latestPostSaleOfferData?.state === 'approved';
  const isSaleComplete = Boolean(latestPostSaleOfferData?.dealerPaymentCompleted);
  const saleCompleteAt = latestPostSaleOfferData?.dealerPaymentCompletedAt || '';

  const isNESettled = Boolean(
    financeStatusData?.isNegativeEquity
    && NE_FUNDS_PAID_STATUSES.has(financeStatusData.financeStatus.seller.status),
  );

  const isPaymentsLoading = useMemo(() => {
    // Order here matters!

    if (!shouldFetchPaymentsData) {
      return false;
    }

    if (!isLatestPostSaleOfferFetched) {
      return true;
    }

    if (!isFinanceStatusFetched) {
      return true;
    }

    if (!shouldFetchPaymentStatus) {
      return false;
    }

    return !isPaymentsStatusesFetched;
  }, [
    isFinanceStatusFetched,
    isLatestPostSaleOfferFetched,
    isPaymentsStatusesFetched,
    shouldFetchPaymentStatus,
    shouldFetchPaymentsData,
  ]);

  const isPostSaleDataLoading = isOfferLoading || isBeneficiaryLoading || isPaymentsLoading;

  const state = useMemo(
    () => ({
      beneficiary: {
        accountNumber,
        error: beneficiaryError,
        loading: isBeneficiaryLoading,
      },
      collectionDetails,
      collectionPreferences,
      enquiryId,
      financeStatusData,
      isNESettled,
      isPostSaleDataLoading,
      isSaleComplete,
      latestPostSaleOfferData,
      offer,
      offerStatus,
      paymentsStatusesData,
      refetchOffer: refetch,
      saleCompleteAt,
      valuationRef,
      vehicle,
      vehicleProfilePath,
      vrm: String(vrm),
    } satisfies VehicleCollectionState),
    [
      accountNumber,
      beneficiaryError,
      isBeneficiaryLoading,
      collectionDetails,
      collectionPreferences,
      enquiryId,
      financeStatusData,
      isNESettled,
      isPostSaleDataLoading,
      isSaleComplete,
      latestPostSaleOfferData,
      offer,
      offerStatus,
      paymentsStatusesData,
      refetch,
      saleCompleteAt,
      valuationRef,
      vehicle,
      vehicleProfilePath,
      vrm,
    ],
  );

  const { isServiceFeeEnabled } = useServiceFeeEnabled();
  const { isServiceFeeExperimentEnabled } = useServiceFeeEnabled({
    dpEnquiryId: enquiryId,
    isEligibleForExperiment: Boolean(enquiryId),
    useExperimentDecision: true,
  });

  const soldForFigure = useMemo(
    () => resolveSellerAmountDetails(state).acceptedBid,
    [state],
  );

  const { fetchServiceFeeData } = useServiceFeeData({
    onError: () => {
      vehicleCollectionState$.next({
        ...vehicleCollectionState$.value,
        serviceFee: null,
        serviceFeeTiers: null,
      });
    },
    onSuccess: ({ fee, tiers }) => {
      vehicleCollectionState$.next({
        ...vehicleCollectionState$.value,
        serviceFee: fee,
        serviceFeeTiers: tiers,
      });
    },
  });

  useEffect(() => {
    const shouldFetch = (isServiceFeeEnabled || isServiceFeeExperimentEnabled) && soldForFigure;
    if (!shouldFetch) {
      return;
    }

    const current = vehicleCollectionState$.value;
    if (current.serviceFee?.price === soldForFigure) {
      return;
    }

    fetchServiceFeeData(soldForFigure, enquiryId);
  }, [isServiceFeeEnabled, isServiceFeeExperimentEnabled, soldForFigure, fetchServiceFeeData, enquiryId]);

  useEffect(() => {
    if (!shouldFetchPaymentsData) {
      return;
    }

    getLatestPostSaleOffer(enquiryId);
    getFinanceStatus(enquiryId);
  }, [enquiryId, getFinanceStatus, getLatestPostSaleOffer, shouldFetchPaymentsData]);

  useEffect(() => {
    if (!shouldFetchPaymentStatus) {
      return;
    }

    getPaymentsStatuses(enquiryId);
  }, [enquiryId, getPaymentsStatuses, shouldFetchPaymentStatus]);

  useEffect(() => {
    vehicleCollectionState$.next({
      ...state,
      serviceFee: vehicleCollectionState$.value.serviceFee,
      serviceFeeTiers: vehicleCollectionState$.value.serviceFeeTiers,
    });
  }, [state]);

  return state;
};
